import {UserSupportUserType} from './../models/User/EnrollTrack/UserSupportUserType';
import {push} from 'connected-react-router';
import {createSnackbar} from 'Core/ducks/snackbar';
import {Async, AsyncResult, isLoaded, ServerError} from 'Core/models/Async';
import {emailValidationRegex, ValidEmail} from 'Core/models/Email';
import {NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {RoleId} from 'Core/models/Role';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {Scope} from 'Shared/constants/Scope';
import {UserUploadFileTypeId} from 'Shared/constants/UserUploadFileType';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {postMultipart} from 'Shared/utilities/fetchHelpers';
import {apiCallGenerator, getValidationSaga} from 'Shared/utilities/sagaApiGenerators';
import {AgentNumberDto, AgentUserTypeDto, UserEntryDto} from 'Users/api/api';
import userApi from 'Users/api/userApi';
import userEntryApi from 'Users/api/userEntryApi';
import {uploadUsers, UserAdministrationActionType} from 'Users/ducks/userAdministration';
import {
  addUser,
  changeEmail,
  changeFirstName,
  changeLastName,
  changePhoneNumber,
  changeUserName,
  clearState,
  showError,
  updateUser,
  UserEntryActionType,
} from 'Users/ducks/userEntry';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {SupportAgentPermissions} from 'Users/models/User/Agent/SupportAgentPermissions';
import {ExistingTaxIdentificationNumber} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {matchValidAgentUserType, ValidAgentUserType} from 'Users/models/User/AgentUserType';
import {isExternalBasicInformation} from 'Users/models/User/BasicInformation';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';
import {ValidUser} from 'Users/models/User/User';
import {matchValidUserTypeByLineOfBusiness} from 'Users/models/User/UserType';
import {UserRouteConstants} from 'Users/Routes';
import {extractApplicationIdsFromScopeSaga} from './applicationIdSaga';
import {Either} from 'fp-ts/lib/Either';

function* validateEmailSaga() {
  yield takeLatest(
    UserEntryActionType.VALIDATE_EMAIL,
    getValidationSaga(ValidEmail.create, changeEmail, userApi.validateEmail, value => emailValidationRegex.test(value))
  );
}

function* validateUsernameSaga() {
  yield takeLatest(
    UserEntryActionType.VALIDATE_USERNAME,
    getValidationSaga(NonEmptyString.create, changeUserName, userApi.validateAllstateUsername)
  );
}

function* validatePhoneNumberSaga() {
  yield takeLatest(
    UserEntryActionType.VALIDATE_PHONE,
    getValidationSaga(ValidPhoneNumber.create, changePhoneNumber, userApi.validatePhoneNumber)
  );
}

function* validateFirstName() {
  yield takeLatest(
    UserEntryActionType.VALIDATE_FIRST_NAME,
    getValidationSaga(NonEmptyString.create, changeFirstName, userApi.validateFirstName)
  );
}
function* validateLastName() {
  yield takeLatest(
    UserEntryActionType.VALIDATE_LAST_NAME,
    getValidationSaga(NonEmptyString.create, changeLastName, userApi.validateLastName)
  );
}
const mapAgentSupportRoles = (permission: SupportAgentPermissions) => {
  const roles = [];

  if (permission.canViewAccounts) {
    roles.push({id: RoleId.AgencySupportWithAccounts});
  }
  if (permission.canViewPolicies) {
    roles.push({id: RoleId.AgencySupportWithPolicies});
  }
  if (permission.canViewCommissionsAnd1099s) {
    roles.push({id: RoleId.AgencySupportWithCommissionsAnd1099s});
  }
  if (permission.canViewPolicyServiceRequest) {
    roles.push({id: RoleId.AgencySupportWithPolicyServiceRequest});
  }
  if (permission.canViewCorrespondence) {
    roles.push({id: RoleId.AgencySupportWithCorrespondence});
  }
  if (permission.canViewClaims) {
    roles.push({id: RoleId.AgencySupportWithClaims});
  }
  if (permission.canViewPendingUnderwritingRequirements) {
    roles.push({id: RoleId.AgencySupportWithUnderwritingRequirements});
  }

  return roles;
};

const mapProducerRoles = (isAgencyAdministrator: boolean) => {
  const roles = [{id: RoleId.Producer}];

  if (isAgencyAdministrator) {
    roles.push({id: RoleId.AgencyAdministrator});
  }

  return roles;
};

const mapAgentUserToDto = (agentUserType: ValidAgentUserType): AgentUserTypeDto =>
  matchValidAgentUserType<AgentUserTypeDto>(agentUserType.value, {
    VIP: vipUserType => ({
      roles: vipUserType.isExternalAdministrator
        ? [{id: RoleId.VIP}, {id: RoleId.AllstateExternalAdministrator}]
        : [{id: RoleId.VIP}],
      isAhl: vipUserType.hasAhlAccess,
      isNewYork: vipUserType.hasNewYorkAccess,
    }),
    FieldSalesLeader: fieldSalesLeaderUserType => ({
      roles: [{id: RoleId.FieldSalesLeader}],
      recruiterCode: {
        isNewYork: fieldSalesLeaderUserType.hasNewYorkAccess,
        isAhl: fieldSalesLeaderUserType.hasAhlAccess,
        value: fieldSalesLeaderUserType.recruiterCode.value,
      },
    }),
    FieldVicePresident: fieldVicePresidentUserType => ({
      roles: [{id: RoleId.FieldVicePresident}],
      fieldSalesLeaders: fieldVicePresidentUserType.fieldSalesLeaders,
    }),
    Agent: agentUserType => ({
      agencyAssociations: agentUserType.agencyAssociations
        .map(aa => ({
          identifier: aa.taxId instanceof ExistingTaxIdentificationNumber ? undefined : aa.taxId.value,
          encryptedIdentifier:
            aa.taxId instanceof ExistingTaxIdentificationNumber ? aa.taxId.encryptedIdentifier : undefined,
          agentNumbers: aa.agentNumbers as AgentNumberDto[],
          roles: mapProducerRoles(aa.isAgencyAdministrator),
        }))
        .toArray(),
    }),
    AgentSupport: agentSupportUserType => ({
      agencyAssociations: agentSupportUserType.agencyAssociations
        .map(aa => ({
          identifier: aa.taxId instanceof ExistingTaxIdentificationNumber ? undefined : aa.taxId.value,
          encryptedIdentifier:
            aa.taxId instanceof ExistingTaxIdentificationNumber ? aa.taxId.encryptedIdentifier : undefined,
          agentNumbers: aa.agentNumbers as AgentNumberDto[],
          roles: mapAgentSupportRoles(aa.supportAgentPermissions),
        }))
        .toArray(),
    }),
  });

const mapUserToDto = (user: ValidUser, id?: string): UserEntryDto => {
  const isUserSupport = user.basicInformation.isUserSupport;
  const userTypes = user.userTypes
    .map(ut =>
      matchValidUserTypeByLineOfBusiness(ut.value, {
        Agent: (agentUserType): any => ({agentUser: mapAgentUserToDto(agentUserType)}),
        Beneficiaries: beneficiaryUserType => ({
          beneficiaryUser: {roles: [{id: beneficiaryUserType.value.beneficiariesRole}]},
        }),
        BosTools: businessOperationsSupportUserType => ({
          businessOperationsSupportUser: {
            roles: [{id: businessOperationsSupportUserType.value.bosToolsRole}],
          },
        }),
        Messaging: messagingUserType => ({
          messagingUser: {roles: [{id: messagingUserType.value.messagingRole}]},
        }),
        EnrollmentTracking: et => ({
          enrollTrackUser: {
            roles: [
              {id: et.value.enrollTrackRole},
              ...(isUserSupport ? [{id: new UserSupportUserType().enrollTrackRole}] : []),
            ],
          },
        }),
        AppEntry: ae => ({applicationEntryUser: {roles: [{id: ae.value.appEntryRole}]}}),
      })
    )
    .reduce((acc, ut) => ({...acc, ...ut}), {});

  const {username, phoneNumber} =
    ((!!id || !isExternalBasicInformation(user.basicInformation)) && user.basicInformation) || {};

  const firstName = user.basicInformation.firstName as Async<Either<StringUnion, NonEmptyString>>;
  const lastName = user.basicInformation.lastName as Async<Either<StringUnion, NonEmptyString>>;
  
  return {
    id,
    firstName: isLoaded(firstName) && E.isRight(firstName.Data) ? firstName.Data.right.value : undefined,
    lastName: isLoaded(lastName) && E.isRight(lastName.Data) ? lastName.Data.right.value : undefined,
    userName: username && isLoaded(username) && E.isRight(username.Data) ? username.Data.right.value : undefined,
    email: user.basicInformation.email.Data.right.address,
    mobilePhone: phoneNumber && E.isRight(phoneNumber.Data) ? phoneNumber.Data.right.phoneNumber : undefined,
    exclusivityStatus:
      user.basicInformation.accountType === 'Internal'
        ? {id: ExclusivityStatus.Internal}
        : {id: ExclusivityStatus.External},
    ...userTypes,
  };
};

function* returnToUsersPage() {
  yield put(clearState());
  yield put(createSnackbar('User has been successfully saved'));
  yield put(push(UserRouteConstants.USERS));
}

function* addUserSaga() {
  yield takeLatest(UserEntryActionType.ADD_USER, function* addUserApi(action: ReturnType<typeof addUser>) {
    const {user} = action.payload;
    const userDto = mapUserToDto(action.payload.user);

    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );
    const encryptedIdentifier = O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined;

    const applicationIds = yield extractApplicationIdsFromScopeSaga(Scope.AddUser);
    const result: AsyncResult<unknown> = yield call(userEntryApi.addUser, userDto, applicationIds, encryptedIdentifier);

    if (E.isRight(result)) {
      yield returnToUsersPage();
    } else {
      yield put(showError(user));
    }
  });
}

function* updateUserSaga() {
  yield takeLatest(UserEntryActionType.UPDATE_USER, function* updateUserApi(action: ReturnType<typeof updateUser>) {
    const {user, userId} = action.payload;
    const userDto = mapUserToDto(user, userId);

    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );
    const encryptedIdentifier = O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined;

    const applicationIds = yield extractApplicationIdsFromScopeSaga(Scope.UpdateUser);
    const result: AsyncResult<unknown> = yield call(
      userEntryApi.updateUser,
      userDto,
      applicationIds,
      encryptedIdentifier
    );

    if (E.isRight(result)) {
      yield returnToUsersPage();
    } else {
      const message = (result.left as ServerError).errorDetails!;

      yield put(createSnackbar(message));
      yield put(showError(user));
    }
  });
}

function* bulkUploadUsersSaga() {
  const uploadFile = (action: ReturnType<typeof uploadUsers>) => {
    const {file, agencyFilters} = action.payload;

    if (action.payload.file.fileTypeId === UserUploadFileTypeId.IndependentAgent) {
      if (agencyFilters) {
        return postMultipart(
          `/users/upload/independent/agency/isnewyork/${agencyFilters.isNewYork}${addFilterParameters({
            ...agencyFilters,
          })}`,
          file.file
        );
      } else {
        return postMultipart(`/users/upload/independent`, file.file);
      }
    } else if (action.payload.file.fileTypeId === UserUploadFileTypeId.EnrollTrackUser) {
      return postMultipart('/users/upload/enrolltrack', file.file);
    } else {
      return postMultipart(`/users/upload?userUploadFileTypeId=${file.fileTypeId}`, file.file);
    }
  };

  yield takeLatest(UserAdministrationActionType.BULK_UPLOAD_USER, apiCallGenerator(uploadFile));
}

export default function*() {
  return yield all([
    validateEmailSaga(),
    validateUsernameSaga(),
    validatePhoneNumberSaga(),
    addUserSaga(),
    updateUserSaga(),
    bulkUploadUsersSaga(),
    validateFirstName(),
    validateLastName(),
  ]);
}
